import { Big } from 'big.js'
import { Measure } from '../types'


enum PressureMeasurementSystem {
  siUnits = 'SI Units',
  metricDerived = 'Metric Derived Units',
  waterBased = 'Water Based Units',
  mercuryBased = 'Mercury Based Units',
  usCustomaryImperial = 'US Customary Units',
  additionalCommon = 'Additional Common Units'
}


const pressureUnits = {
  pascal: {
    displayName: 'Pascal',
    codeName: 'pascal',
    url: 'pascal',
    symbol: 'Pa',
    toBaseUnit: (value: Big): Big => value,
    fromBaseUnit: (value: Big): Big => value,
    system: PressureMeasurementSystem.siUnits
  },
  newton_per_square_meter: {
    displayName: 'Newton per Square Meter',
    codeName: 'newton_per_square_meter',
    url: 'newton-per-square-meter',
    symbol: 'N/m²',
    toBaseUnit: (value: Big): Big => value,
    fromBaseUnit: (value: Big): Big => value,
    system: PressureMeasurementSystem.siUnits
  },

  millibar: {
    displayName: 'Millibar',
    codeName: 'millibar',
    url: 'millibar',
    symbol: 'mbar',
    toBaseUnit: (value: Big): Big => value.times(100),
    fromBaseUnit: (value: Big): Big => value.div(100),
    system: PressureMeasurementSystem.metricDerived
  },
  bar: {
    displayName: 'Bar',
    codeName: 'bar',
    url: 'bar',
    symbol: 'bar',
    toBaseUnit: (value: Big): Big => value.times(100000),
    fromBaseUnit: (value: Big): Big => value.div(100000),
    system: PressureMeasurementSystem.metricDerived
  },
  kilogram_force_per_square_centimeter: {
    displayName: 'Kilogram-force per Square Centimeter',
    codeName: 'kilogram_force_per_square_centimeter',
    url: 'kilogram-force-per-square-centimeter',
    symbol: 'kgf/cm²',
    toBaseUnit: (value: Big): Big => value.div(1.019716212978E-05),
    fromBaseUnit: (value: Big): Big => value.times(1.019716212978E-05),
    system: PressureMeasurementSystem.metricDerived
  },
  kilogram_force_per_square_meter: {
    displayName: 'Kilogram-force per Square Meter',
    codeName: 'kilogram_force_per_square_meter',
    url: 'kilogram-force-per-square-meter',
    symbol: 'Kgf/m²',
    toBaseUnit: (value: Big): Big => value.div(0.1019716212978),
    fromBaseUnit: (value: Big): Big => value.times(0.1019716212978),
    system: PressureMeasurementSystem.metricDerived
  },
  newton_per_square_centimeter: {
    displayName: 'Newton per Square Centimeter',
    codeName: 'newton_per_square_centimeter',
    url: 'newton-per-square-centimeter',
    symbol: 'N/cm²',
    toBaseUnit: (value: Big): Big => value.div(0.0001),
    fromBaseUnit: (value: Big): Big => value.times(0.0001),
    system: PressureMeasurementSystem.metricDerived
  },


  millimeter_water_four_degrees: {
    displayName: 'Millimeter Water (4 °C)',
    codeName: 'millimeter_water_four_degrees',
    url: 'millimeter-water-four-degrees',
    symbol: 'mmH20',
    toBaseUnit: (value: Big): Big => value.div(0.101974428892211),
    fromBaseUnit: (value: Big): Big => value.times(0.101974428892211),
    system: PressureMeasurementSystem.waterBased
  },
  centimeter_water_four_degrees: {
    displayName: 'Centimeter Water (4 °C)',
    codeName: 'centimeter_water_four_degrees',
    url: 'centimeter-water-four-degrees',
    symbol: 'cmH2O',
    toBaseUnit: (value: Big): Big => value.div(0.0101974428892211),
    fromBaseUnit: (value: Big): Big => value.times(0.0101974428892211),
    system: PressureMeasurementSystem.waterBased
  },
  meter_water_four_degrees: {
    displayName: 'Meter Water (4 °C)',
    codeName: 'meter_water_four_degrees',
    url: 'meter-water-four-degrees',
    symbol: 'mH2O',
    toBaseUnit: (value: Big): Big => value.div(0.000101974428892211),
    fromBaseUnit: (value: Big): Big => value.times(0.000101974428892211),
    system: PressureMeasurementSystem.waterBased
  },
  inch_water_four_degrees: {
    displayName: 'Inch Water (4 °C)',
    codeName: 'inch_water_four_degrees',
    url: 'inch-water-four-degrees',
    symbol: 'inH2O',
    toBaseUnit: (value: Big): Big => value.div(0.0040147421),
    fromBaseUnit: (value: Big): Big => value.times(0.0040147421),
    system: PressureMeasurementSystem.waterBased
  },
  foot_water_four_degrees: {
    displayName: 'Foot Water (4 °C)',
    codeName: 'foot_water_four_degrees',
    url: 'foot-water-four-degrees',
    symbol: 'ftH2O',
    toBaseUnit: (value: Big): Big => value.div(0.0003345623),
    fromBaseUnit: (value: Big): Big => value.times(0.0003345623),
    system: PressureMeasurementSystem.waterBased
},


  millimeter_mercury: {
    displayName: 'Millimeter Mercury',
    codeName: 'millimeter_mercury',
    url: 'millimeter-mercury',
    symbol: 'mmHg',
    toBaseUnit: (value: Big): Big => value.div(0.00750063755419211),
    fromBaseUnit: (value: Big): Big => value.times(0.00750063755419211),
    system: PressureMeasurementSystem.mercuryBased
  },
  inch_mercury: {
    displayName: 'Inch Mercury',
    codeName: 'inch_mercury',
    url: 'inch-mercury',
    symbol: 'inHg',
    toBaseUnit: (value: Big): Big => value.div(0.000295300586466965),
    fromBaseUnit: (value: Big): Big => value.times(0.000295300586466965),
    system: PressureMeasurementSystem.mercuryBased
  },
  torr: {
    displayName: 'Torr',
    codeName: 'torr',
    url: 'torr',
    symbol: 'Torr',
    toBaseUnit: (value: Big): Big => value.div(0.007500616827041697),
    fromBaseUnit: (value: Big): Big => value.times(0.007500616827041697),
    system: PressureMeasurementSystem.mercuryBased
  },


  pound_per_square_inch: {
    displayName: 'Pound per Square Inch',
    codeName: 'pound_per_square_inch',
    url: 'pound-per-square-inch',
    symbol: 'psi',
    toBaseUnit: (value: Big): Big => value.div(0.000145037737731556),
    fromBaseUnit: (value: Big): Big => value.times(0.000145037737731556),
    system: PressureMeasurementSystem.usCustomaryImperial
  },
  pound_per_square_foot: {
    displayName: 'Pound per Square Foot',
    codeName: 'pound_per_square_foot',
    url: 'pound-per-square-foot',
    symbol: 'psf',
    toBaseUnit: (value: Big): Big => value.div(0.0208854342333441),
    fromBaseUnit: (value: Big): Big => value.times(0.0208854342333441),
    system: PressureMeasurementSystem.usCustomaryImperial
  },


  atmosphere: {
    displayName: 'Atmosphere',
    codeName: 'atmosphere',
    url: 'atmosphere',
    symbol: 'atm',
    toBaseUnit: (value: Big): Big => value.times(101325),
    fromBaseUnit: (value: Big): Big => value.div(101325),
    system: PressureMeasurementSystem.additionalCommon
  }
}

export const pressure: Measure = {
  displayName: 'Pressure',
  codeName: 'pressure',
  url: 'pressure',
  units: pressureUnits,
  baseUnit: pressureUnits.pascal,
  defaultFromUnit: pressureUnits.pascal,
  defaultToUnit: pressureUnits.bar
}
